import React, {
  TextareaHTMLAttributes,
  useCallback,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import { useAutoHeight, useForwardedRef } from '@adsk/alloy-react-helpers';
import theme, {
  getInteractiveStyles,
  stylePropType,
  DetailedStylableComponent,
} from '@adsk/alloy-react-theme';

const RIGHT_PADDING = 16;
const StyledDiv = styledC.div`
  position: relative;
  margin: 0;
  padding: 0;
`;

const StyledCount = styledC.div<{
  disabled?: boolean;
  $error?: boolean;
  $rightPadding?: number;
}>`
  color: ${theme.tokens.colors.text.secondary.value};
  ${(props) => props.$rightPadding && `right: ${props.$rightPadding}px`};
  pointer-events: none;
  position: absolute;
  bottom: 0;
  transform: translateY(-50%);

  ${(props) => props.disabled && `color: ${theme.tokens.colors.text.inactive};`}
  ${(props) =>
    props.$error && `color: ${theme.tokens.colors.text.error.value};`}
`;

export type TextareaProps = DetailedStylableComponent<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
> & {
  error?: boolean;
  disabled?: boolean;
  maxHeight?: number | string;
  minHeight?: number | string;
  value?: string;
  preventAutoHeight?: boolean;
  softLimit?: number;
  showCountThreshold?: number;
  showCount?: boolean;
};
/**
 * Textarea component that expands with text content.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to HTML textarea element.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      style,
      className,
      error = false,
      disabled = false,
      maxHeight = '100%',
      minHeight = 36,
      onChange,
      value,
      preventAutoHeight,
      softLimit,
      maxLength,
      showCountThreshold = 15,
      defaultValue,
      showCount,
      ...props
    },
    ref,
  ) => {
    const textareaRef = useForwardedRef(ref);
    const [height, updateHeight] = useAutoHeight(textareaRef);
    const [charCount, setCharCount] = useState(0);
    const limit = useMemo(
      () => showCount !== false && (softLimit || maxLength),
      [showCount, softLimit, maxLength],
    );

    const showLimit = useMemo(
      () =>
        limit &&
        (!showCountThreshold ||
          (showCountThreshold && limit - charCount <= showCountThreshold)),
      [limit, showCountThreshold, charCount],
    );

    useLayoutEffect(() => {
      if (!preventAutoHeight) {
        updateHeight();
      }
    });

    useLayoutEffect(() => {
      if (value !== undefined && value !== null) {
        setCharCount(value.length);
      }
    }, [value]);

    useLayoutEffect(() => {
      if (defaultValue !== undefined && defaultValue !== null) {
        setCharCount(defaultValue.toString().length);
      }
      // we only want to do this on mount since it's the default value
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (value === undefined || value === null) {
          if (event.target.value) {
            setCharCount(event.target.value.length);
          } else {
            setCharCount(0);
          }
        }

        updateHeight();
        onChange?.(event);
      },
      [onChange, updateHeight, value],
    );

    return (
      <StyledDiv>
        <textarea
          {...props}
          className={className}
          disabled={disabled}
          ref={textareaRef}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          maxLength={maxLength}
          css={[
            {
              ...theme.typography.labelMedium,
              color: theme.tokens.colors.text.primary.value,
              border: `1px solid ${theme.tokens.colors.border.value}`,
              borderRadius: theme.borderRadius.small,
              boxSizing: 'border-box',
              height,
              maxHeight,
              minHeight,
              outline: 'none',
              padding: `${theme.spacing.XS}px ${theme.spacing.S}px`,
              resize: 'none',

              ...getInteractiveStyles(disabled),

              '&::placeholder': {
                color: theme.tokens.colors.text.tertiary.value,
              },

              '&:disabled': {
                background:
                  theme.tokens.colors.background.neutral.inactive.value,
                borderColor: theme.tokens.colors.border.inactive.value,
                boxShadow: 'none',
                color: theme.tokens.colors.text.inactive.value,
                cursor: 'not-allowed',
              },

              ...(!error && {
                '&:focus:not(:active)': {
                  borderColor: theme.tokens.colors.border.focused.value,
                },
              }),

              ...(error && {
                borderColor: theme.tokens.colors.border.error.value,
              }),
              ...(showLimit && {
                paddingBottom: 20,
              }),
            },
            style,
          ]}
        />
        {!!showLimit && (
          <StyledCount
            disabled={disabled}
            $error={error}
            $rightPadding={RIGHT_PADDING}
            data-testid="Textarea__count"
          >
            {`${charCount}/${limit}`}
          </StyledCount>
        )}
      </StyledDiv>
    );
  },
);

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Max height in pixels or percent (applied via CSS) */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Min height in pixels or percent (applied via CSS) */
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** String value of the textarea */
  value: PropTypes.string,
  /** Function executed on text content change */
  onChange: PropTypes.func,
  /** Text to display when input is empty */
  placeholder: PropTypes.string,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
};

export default Textarea;
